import { useQuery } from '@tanstack/react-query';
import brandMetricsApi from '../api/brandMetrics';

export const brandMetricsQueryKeys = {
  brands: ['brandMetrics', 'brands'],
  panel: (brandId, start, end) => ['brandMetrics', 'panel', String(brandId), start, end],
  myPanel: (start, end) => ['brandMetrics', 'my-panel', start, end],
  overview: (start, end) => ['brandMetrics', 'overview', start, end],
  campaigns: (brandId, start, end) =>
    ['brandMetrics', 'campaigns', String(brandId), start, end],
  myCampaigns: (start, end) => ['brandMetrics', 'my-campaigns', start, end],
  catalog: (brandId, start, end) => ['brandMetrics', 'catalog', String(brandId), start, end],
  myCatalog: (start, end) => ['brandMetrics', 'my-catalog', start, end],
  catalogHealth: (brandId) => ['brandMetrics', 'catalog-health', String(brandId)],
  myCatalogHealth: () => ['brandMetrics', 'my-catalog-health'],
  dailyChecklist: (brandId, days) => ['brandMetrics', 'daily-checklist', String(brandId), days],
  leads: (brandId, start, end) => ['brandMetrics', 'leads', String(brandId), start, end],
  myLeads: (start, end) => ['brandMetrics', 'my-leads', start, end],
  customerAnalytics: (brandId, start, end) =>
    ['brandMetrics', 'customer-analytics', String(brandId), start, end],
  myCustomerAnalytics: (start, end) => ['brandMetrics', 'my-customer-analytics', start, end],
};

const STALE_MS = 5 * 60 * 1000; // los datos se sincronizan 2 veces por día

export function useConfiguredBrandsQuery(options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.brands,
    queryFn: async () => {
      const data = await brandMetricsApi.getConfiguredBrands();
      return data?.brands ?? [];
    },
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandPanelQuery(brandId, start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.panel(brandId, start, end),
    queryFn: () => brandMetricsApi.getBrandPanel(brandId, start, end),
    enabled: Boolean(brandId && start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useMyBrandPanelQuery(start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.myPanel(start, end),
    queryFn: () => brandMetricsApi.getMyPanel(start, end),
    enabled: Boolean(start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandCampaignsQuery(brandId, start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.campaigns(brandId, start, end),
    queryFn: () => brandMetricsApi.getBrandCampaigns(brandId, start, end),
    enabled: Boolean(brandId && start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useMyCampaignsQuery(start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.myCampaigns(start, end),
    queryFn: () => brandMetricsApi.getMyCampaigns(start, end),
    enabled: Boolean(start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandCatalogQuery(brandId, start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.catalog(brandId, start, end),
    queryFn: () => brandMetricsApi.getBrandCatalog(brandId, start, end),
    enabled: Boolean(brandId && start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useMyCatalogQuery(start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.myCatalog(start, end),
    queryFn: () => brandMetricsApi.getMyCatalog(start, end),
    enabled: Boolean(start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandCatalogHealthQuery(brandId, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.catalogHealth(brandId),
    queryFn: () => brandMetricsApi.getBrandCatalogHealth(brandId),
    enabled: Boolean(brandId),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useMyCatalogHealthQuery(options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.myCatalogHealth(),
    queryFn: () => brandMetricsApi.getMyCatalogHealth(),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandDailyChecklistQuery(brandId, days = 30, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.dailyChecklist(brandId, days),
    queryFn: () => brandMetricsApi.getBrandDailyChecklist(brandId, days),
    enabled: Boolean(brandId),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandLeadsQuery(brandId, start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.leads(brandId, start, end),
    queryFn: () => brandMetricsApi.getBrandLeads(brandId, start, end),
    enabled: Boolean(brandId),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useMyBrandLeadsQuery(start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.myLeads(start, end),
    queryFn: () => brandMetricsApi.getMyBrandLeads(start, end),
    enabled: Boolean(start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandCustomerAnalyticsQuery(brandId, start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.customerAnalytics(brandId, start, end),
    queryFn: () => brandMetricsApi.getBrandCustomerAnalytics(brandId, start, end),
    enabled: Boolean(brandId && start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useMyCustomerAnalyticsQuery(start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.myCustomerAnalytics(start, end),
    queryFn: () => brandMetricsApi.getMyCustomerAnalytics(start, end),
    enabled: Boolean(start && end),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useBrandsOverviewQuery(start, end, options = {}) {
  return useQuery({
    queryKey: brandMetricsQueryKeys.overview(start, end),
    queryFn: async () => {
      const data = await brandMetricsApi.getBrandsOverview(start, end);
      return data?.rows ?? [];
    },
    enabled: Boolean(start && end),
    staleTime: STALE_MS,
    ...options,
  });
}
